import { Stream } from "node:stream";
import { RavenCommand } from "../../Http/RavenCommand.js";
import { ServerNode } from "../../Http/ServerNode.js";
import { HttpRequestParameters } from "../../Primitives/Http.js";
import { getHeaders } from "../../Utility/HttpUtil.js";
import { TypeUtil } from "../../Utility/TypeUtil.js";
import { throwError } from "../../Exceptions/index.js";
import { DocumentConventions } from "../Conventions/DocumentConventions.js";
import { COUNTERS } from "../../Constants.js";
import { HashCalculator } from "../Queries/HashCalculator.js";
import { IRavenObject } from "../../Types/IRavenObject.js";
import { TimeSeriesRange } from "../Operations/TimeSeries/TimeSeriesRange.js";
import { DateUtil } from "../../Utility/DateUtil.js";
import { readToEnd } from "../../Utility/StreamUtil.js";
import { ObjectUtil } from "../../Utility/ObjectUtil.js";
import { AbstractTimeSeriesRange } from "../Operations/TimeSeries/AbstractTimeSeriesRange.js";
import { TimeSeriesTimeRange } from "../Operations/TimeSeries/TimeSeriesTimeRange.js";
import { TimeSeriesCountRange } from "../Operations/TimeSeries/TimeSeriesCountRange.js";
import { TransactionMode } from "../Session/TransactionMode.js";

export interface GetDocumentsCommandCounterOptions {
    counterIncludes?: string[];
    includeAllCounters?: boolean;
}

export interface GetDocumentsCommandOptionsBase extends GetDocumentsCommandCounterOptions {
    conventions: DocumentConventions;
}

export interface GetDocumentsByIdCommandOptions
    extends GetDocumentsCommandOptionsBase {
    id: string;
    includes?: string[];
    metadataOnly?: boolean;
}

export interface GetDocumentsByIdsCommandOptions
    extends GetDocumentsCommandOptionsBase {
    ids: string[];
    includes?: string[];
    metadataOnly?: boolean;
    timeSeriesIncludes?: AbstractTimeSeriesRange[];
    revisionsIncludesByChangeVector?: string[];
    revisionIncludeByDateTimeBefore?: Date;
    compareExchangeValueIncludes?: string[];
}

export interface GetDocumentsStartingWithOptions
    extends GetDocumentsCommandOptionsBase {
    start: number;
    pageSize: number;
    startsWith?: string;
    startsAfter?: string;
    matches?: string;
    exclude?: string;
    metadataOnly?: boolean;
}

export interface GetDocumentsResult {
    includes: IRavenObject;
    results: any[];
    counterIncludes: IRavenObject;
    revisionIncludes: any[];
    timeSeriesIncludes: IRavenObject;
    compareExchangeValueIncludes: IRavenObject;
    nextPageStart: number;
}

export class GetDocumentsCommand extends RavenCommand<GetDocumentsResult> {

    private readonly _id: string;

    private readonly _ids: string[];
    private readonly _includes: string[];

    private _counters: string[];
    private _includeAllCounters: boolean;
    private _txMode: TransactionMode;

    private _timeSeriesIncludes: AbstractTimeSeriesRange[];
    private _revisionsIncludeByChangeVector: string[];
    private _revisionsIncludeByDateTime: Date;
    private _compareExchangeValueIncludes: string[];

    private readonly _metadataOnly: boolean;

    private readonly _startsWith: string;
    private readonly _matches: string;
    private readonly _start: number;
    private readonly _pageSize: number;
    private readonly _exclude: string;
    private readonly _startAfter: string;

    private readonly _conventions: DocumentConventions;

    public constructor(
        opts: GetDocumentsByIdCommandOptions | GetDocumentsByIdsCommandOptions | GetDocumentsStartingWithOptions) {
        super();

        this._conventions = opts.conventions;

        // eslint-disable-next-line no-prototype-builtins
        if (opts.hasOwnProperty("id")) {
            opts = opts as GetDocumentsByIdCommandOptions;
            if (!opts.id) {
                throwError("InvalidArgumentException", "id cannot be null");
            }
            this._id = opts.id;
            this._includes = opts.includes;
            this._metadataOnly = opts.metadataOnly;
            // eslint-disable-next-line no-prototype-builtins
        } else if (opts.hasOwnProperty("ids")) {
            opts = opts as GetDocumentsByIdsCommandOptions;
            if (!opts.ids || opts.ids.length === 0) {
                throwError("InvalidArgumentException", "Please supply at least one id");
            }
            this._ids = opts.ids;
            this._includes = opts.includes;
            this._metadataOnly = opts.metadataOnly;
            this._timeSeriesIncludes = opts.timeSeriesIncludes;
            this._compareExchangeValueIncludes = opts.compareExchangeValueIncludes;
            this._revisionsIncludeByDateTime = opts.revisionIncludeByDateTimeBefore;
            this._revisionsIncludeByChangeVector = opts.revisionsIncludesByChangeVector;
            // eslint-disable-next-line no-prototype-builtins
        } else if (opts.hasOwnProperty("start") && opts.hasOwnProperty("pageSize")) {
            opts = opts as GetDocumentsStartingWithOptions;
            this._start = opts.start;
            this._pageSize = opts.pageSize;

            // eslint-disable-next-line no-prototype-builtins
            if (opts.hasOwnProperty("startsWith")) {
                if (!opts.startsWith) {
                    throwError("InvalidArgumentException", "startWith cannot be null");
                }
                this._startsWith = opts.startsWith;
                this._startAfter = opts.startsAfter;
                this._matches = opts.matches;
                this._exclude = opts.exclude;
                this._metadataOnly = opts.metadataOnly;
            }
        }

        // eslint-disable-next-line no-prototype-builtins
        if (opts.hasOwnProperty("includeAllCounters" as keyof GetDocumentsCommandCounterOptions)) {
            this._includeAllCounters = opts.includeAllCounters;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (opts.hasOwnProperty("counterIncludes" as keyof GetDocumentsCommandCounterOptions)) {
            const counters = opts.counterIncludes as string[];
            if (!counters) {
                throwError("InvalidArgumentException", "CounterIncludes cannot be null.");
            }

            this._counters = counters;
        }
    }

    public createRequest(node: ServerNode): HttpRequestParameters {
        const uriPath = `${node.url}/databases/${node.database}/docs?`;

        let query = "";

        if (this._txMode === "ClusterWide") {
            query += "&txMode=ClusterWide";
        }

        if (!TypeUtil.isNullOrUndefined(this._start)) {
            query += `&start=${this._start}`;
        }

        if (this._pageSize) {
            query += `&pageSize=${this._pageSize}`;
        }

        if (this._metadataOnly) {
            query += "&metadataOnly=true";
        }

        if (this._startsWith) {
            query += `&startsWith=${encodeURIComponent(this._startsWith)}`;

            if (this._matches) {
                query += `&matches=${encodeURIComponent(this._matches)}`;
            }

            if (this._exclude) {
                query += `&exclude=${encodeURIComponent(this._exclude)}`;
            }

            if (this._startAfter) {
                query += `&startAfter=${this._startAfter}`;
            }
        }

        if (this._includes) {
            for (const include of this._includes) {
                query += `&include=${encodeURIComponent(include)}`;
            }
        }

        if (this._includeAllCounters) {
            query += `&counter=${COUNTERS.ALL}`;
        } else if (this._counters && this._counters.length) {
            for (const counter of this._counters) {
                query += `&counter=${encodeURIComponent(counter)}`;
            }
        }

        if (this._timeSeriesIncludes) {
            for (const tsInclude of this._timeSeriesIncludes) {
                if ("from" in tsInclude) {
                    const range = tsInclude as TimeSeriesRange;
                    query += "&timeseries=" + this._urlEncode(range.name)
                        + "&from=" + (range.from ? DateUtil.utc.stringify(range.from) : "")
                        + "&to=" + (range.to ? DateUtil.utc.stringify(range.to) : "");
                } else if ("time" in tsInclude) {
                    const timeRange = tsInclude as TimeSeriesTimeRange;
                    query +=
                        "&timeseriestime="
                        + this._urlEncode(timeRange.name)
                        + "&timeType="
                        + this._urlEncode(timeRange.type)
                        + "&timeValue="
                        + timeRange.time.value
                        + "&timeUnit="
                        + this._urlEncode(timeRange.time.unit);
                } else if ("count" in tsInclude) {
                    const countRange = tsInclude as TimeSeriesCountRange;
                    query +=
                        "&timeseriescount="
                        + this._urlEncode(countRange.name)
                        + "&countType="
                        + this._urlEncode(countRange.type)
                        + "&countValue="
                        + countRange.count;
                } else {
                    throwError("InvalidArgumentException", "Unexpected TimeSeries range: " + tsInclude);
                }
            }
        }

        if (this._revisionsIncludeByChangeVector) {
            for (const changeVector of this._revisionsIncludeByChangeVector) {
                query += "&revisions=" + this._urlEncode(changeVector);
            }
        }

        if (this._revisionsIncludeByDateTime) {
            query += "&revisionsBefore=" + this._urlEncode(DateUtil.utc.stringify(this._revisionsIncludeByDateTime));
        }

        if (this._compareExchangeValueIncludes) {
            for (const compareExchangeValue of this._compareExchangeValueIncludes) {
                query += "&cmpxchg=" + this._urlEncode(compareExchangeValue);
            }
        }

        let request: HttpRequestParameters = { method: "GET", uri: uriPath + query };

        if (this._id) {
            request.uri += `&id=${encodeURIComponent(this._id)}`;
        } else if (this._ids) {
            request = this.prepareRequestWithMultipleIds(request, this._ids);
        }

        return request;
    }

    public prepareRequestWithMultipleIds(request: HttpRequestParameters, ids: string[]): HttpRequestParameters {
        const uniqueIds = new Set<string>(ids);

        // if it is too big, we fallback to POST (note that means that we can't use the HTTP cache any longer)
        // we are fine with that, requests to load > 1024 items are going to be rare
        const isGet: boolean = Array.from(uniqueIds)
            .filter(x => x)
            .map(x => x.length)
            .reduce((result, next) => result + next, 0) < 1024;

        let newUri = request.uri;
        if (isGet) {
            for (const x of uniqueIds) {
                newUri += `&id=${encodeURIComponent(x || "")}`;
            }

            return { method: "GET", uri: newUri };
        } else {
            const body = this._serializer
                .serialize({ ids: [...uniqueIds] });

            const calculateHash = GetDocumentsCommand._calculateHash(uniqueIds);
            newUri += `&loadHash=${encodeURIComponent(calculateHash)}`;

            return {
                uri: newUri,
                method: "POST",
                headers: getHeaders()
                    .typeAppJson()
                    .build(),
                body
            };
        }
    }

    private static _calculateHash(uniqueIds: Set<string>): string {
        const hasher = new HashCalculator();

        for (const x of uniqueIds) {
            hasher.write(x);
        }

        return hasher.getHash();
    }

    public async setResponseAsync(bodyStream: Stream, fromCache: boolean): Promise<string> {
        if (!bodyStream) {
            this.result = null;
            return;
        }

        let body: string = null;
        this.result =
            await GetDocumentsCommand.parseDocumentsResultResponseAsync(
                bodyStream, this._conventions, b => body = b);

        return body as string;
    }

    public static async parseDocumentsResultResponseAsync(
        bodyStream: Stream,
        conventions: DocumentConventions,
        bodyCallback?: (body: string) => void): Promise<GetDocumentsResult> {

        const body = await readToEnd(bodyStream);
        bodyCallback?.(body);

        const parsedJson = JSON.parse(body);

        return GetDocumentsCommand._mapToLocalObject(parsedJson, conventions);
    }

    private static _mapToLocalObject(json: any, conventions: DocumentConventions): GetDocumentsResult {
        return {
            results: json.Results.map(x => ObjectUtil.transformDocumentKeys(x, conventions)),
            includes: ObjectUtil.mapIncludesToLocalObject(json.Includes, conventions),
            compareExchangeValueIncludes: ObjectUtil.mapCompareExchangeToLocalObject(json.CompareExchangeValueIncludes),
            timeSeriesIncludes: ObjectUtil.mapTimeSeriesIncludesToLocalObject(json.TimeSeriesIncludes),
            counterIncludes: ObjectUtil.mapCounterIncludesToLocalObject(json.CounterIncludes),
            revisionIncludes: json.RevisionIncludes,
            nextPageStart: json.NextPageStart
        };
    }

    public get isReadRequest(): boolean {
        return true;
    }

    public set transactionMode(mode: TransactionMode) {
        this._txMode = mode;
    }
}
